/**
 * Users Hooks
 *
 * TanStack Query hooks for user management operations.
 * Includes current user profile and admin user CRUD.
 */

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useApi } from '../lib/hooks/useApi';
import type {
  UserListItem,
  UserListQuery,
  UserLookup,
  UserDetail,
  UpdateUser,
  UpdateProfile,
} from '@guild/shared';

// Pagination response type
interface PaginatedResponse<T> {
  data: T[];
  pagination: {
    page: number;
    limit: number;
    total: number;
    totalPages: number;
  };
}

// Query keys
export const userKeys = {
  all: ['users'] as const,
  lists: () => [...userKeys.all, 'list'] as const,
  list: (filters: Partial<UserListQuery>) => [...userKeys.lists(), filters] as const,
  lookup: () => [...userKeys.all, 'lookup'] as const,
  details: () => [...userKeys.all, 'detail'] as const,
  detail: (id: string) => [...userKeys.details(), id] as const,
  me: () => ['me'] as const,
  syncStatus: () => [...userKeys.all, 'sync-status'] as const,
  syncAudit: () => [...userKeys.all, 'sync-audit'] as const,
};

// ============================================
// CURRENT USER HOOKS (/api/me)
// ============================================

/**
 * GET /api/me: the user's profile plus the org's IANA time zone (Phase 41A).
 * `orgTimezone` is optional because the previous API omits it (spec S16).
 * `UserDetail` itself stays as is: GET /api/users/:id shares it.
 */
export type CurrentUser = UserDetail & { orgTimezone?: string };

/**
 * Fetch current user's profile
 */
export function useCurrentUser() {
  const api = useApi();

  return useQuery({
    queryKey: userKeys.me(),
    queryFn: () => api.get<CurrentUser>('/api/me'),
    staleTime: 5 * 60 * 1000, // 5 minutes
  });
}

/**
 * Update current user's profile
 */
export function useUpdateProfile() {
  const api = useApi();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: UpdateProfile) => api.put<UserDetail>('/api/me', data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: userKeys.me() });
    },
  });
}

/**
 * Request password reset for current user
 */
export function useRequestPasswordReset() {
  const api = useApi();

  return useMutation({
    mutationFn: () => api.post<{ message: string }>('/api/me/password-reset', {}),
  });
}

// ============================================
// ADMIN USER MANAGEMENT HOOKS (/api/users)
// ============================================

/**
 * Fetch paginated users list
 */
export function useUsers(options?: Partial<UserListQuery>) {
  const api = useApi();

  return useQuery({
    queryKey: userKeys.list(options ?? {}),
    queryFn: async () => {
      const params = new URLSearchParams();

      if (options?.page) params.set('page', String(options.page));
      if (options?.limit) params.set('limit', String(options.limit));
      if (options?.search) params.set('search', options.search);
      if (options?.role) params.set('role', options.role);
      if (options?.isActive !== undefined) params.set('isActive', String(options.isActive));
      if (options?.sortBy) params.set('sortBy', options.sortBy);
      if (options?.sortOrder) params.set('sortOrder', options.sortOrder);

      const queryString = params.toString();
      const endpoint = queryString ? `/api/users?${queryString}` : '/api/users';

      return api.get<PaginatedResponse<UserListItem>>(endpoint);
    },
  });
}

/**
 * Fetch lightweight users list for dropdowns
 */
export function useUserLookup() {
  const api = useApi();

  return useQuery({
    queryKey: userKeys.lookup(),
    queryFn: () => api.get<UserLookup[]>('/api/users/lookup'),
    staleTime: 5 * 60 * 1000,
  });
}

/**
 * Fetch users available for operator linking
 * Returns operator-role users not already linked to an operator
 */
export function useUsersForOperatorLinking() {
  const api = useApi();

  return useQuery({
    queryKey: [...userKeys.all, 'for-operator-linking'] as const,
    queryFn: () => api.get<UserLookup[]>('/api/users/for-operator-linking'),
    staleTime: 5 * 60 * 1000,
  });
}

/**
 * Fetch single user details
 */
export function useUser(id: string | null) {
  const api = useApi();

  return useQuery({
    queryKey: userKeys.detail(id ?? ''),
    queryFn: () => api.get<UserDetail>(`/api/users/${id}`),
    enabled: !!id,
  });
}

/**
 * Update user mutation (admin)
 *
 * Phase 19.S frontend migration: also invalidates the /api/me cache so that
 * if an admin updates their OWN role/isActive, the role-gated UI (sidebar
 * items, badges, super-admin panels) re-renders without a forced logout.
 * Over-broad on the common case (admin updating someone else's role) but
 * the /api/me query is cheap and the correctness win justifies it.
 */
export function useUpdateUser() {
  const api = useApi();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: UpdateUser }) =>
      api.put<UserDetail>(`/api/users/${id}`, data),
    onSuccess: (_, variables) => {
      // Invalidate list and specific detail
      queryClient.invalidateQueries({ queryKey: userKeys.lists() });
      queryClient.invalidateQueries({ queryKey: userKeys.detail(variables.id) });
      queryClient.invalidateQueries({ queryKey: userKeys.lookup() });
      // Phase 19.S: refresh DB-canonical role for the current user.
      queryClient.invalidateQueries({ queryKey: userKeys.me() });
    },
  });
}

/**
 * Deactivate user mutation (admin)
 *
 * Phase 19.S: invalidates /api/me so a self-deactivation (rare) propagates
 * to UI without a forced logout. (`userKeys.all` is the parent of `userKeys.me()`,
 * but `userKeys.me()` lives at the top level `['me']` — invalidate it explicitly.)
 */
export function useDeactivateUser() {
  const api = useApi();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id: string) => api.delete<{ id: string }>(`/api/users/${id}`),
    onSuccess: () => {
      // Invalidate all user queries
      queryClient.invalidateQueries({ queryKey: userKeys.all });
      // Phase 19.S: refresh DB-canonical user for the current viewer.
      queryClient.invalidateQueries({ queryKey: userKeys.me() });
    },
  });
}

/**
 * Reactivate user mutation (admin)
 *
 * Phase 19.S: invalidates /api/me so a reactivation of the current viewer
 * (e.g., super_admin restoring their own access) propagates without re-login.
 */
export function useReactivateUser() {
  const api = useApi();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id: string) =>
      api.put<UserDetail>(`/api/users/${id}`, { isActive: true }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: userKeys.all });
      // Phase 19.S: refresh DB-canonical user for the current viewer.
      queryClient.invalidateQueries({ queryKey: userKeys.me() });
    },
  });
}

// ============================================
// AUTH0 SYNC HOOKS (super_admin only)
// ============================================

// Auth0 sync response types
interface Auth0SyncStatus {
  cacheSize: number;
  dbUsers: {
    total: number;
    active: number;
    inactive: number;
  };
}

export interface Auth0SyncConflict {
  dbId: string;
  email: string;
  dbRole: string;
  auth0Role: string | undefined;
  dbActive: boolean;
  auth0Blocked: boolean | undefined;
}

export interface Auth0SyncAudit {
  summary: {
    total: number;
    synced: number;
    conflicts: number;
    errors: number;
  };
  synced: Array<{ dbId: string; email: string; role: string; isActive: boolean }>;
  roleConflicts: Auth0SyncConflict[];
  auth0Errors: Array<{ dbId: string; email: string; error: string }>;
}

/**
 * Fetch Auth0 sync status (super_admin only)
 */
export function useAuth0SyncStatus(enabled: boolean) {
  const api = useApi();

  return useQuery({
    queryKey: userKeys.syncStatus(),
    queryFn: () => api.get<Auth0SyncStatus>('/api/admin/auth0-sync/status'),
    enabled,
    staleTime: 30 * 1000, // 30 seconds
  });
}

/**
 * Run Auth0 sync audit (super_admin only)
 * Set enabled=true to trigger the audit fetch.
 */
export function useAuth0SyncAudit(enabled: boolean) {
  const api = useApi();

  return useQuery({
    queryKey: userKeys.syncAudit(),
    queryFn: () => api.get<Auth0SyncAudit>('/api/admin/auth0-sync/audit'),
    enabled,
    staleTime: 60 * 1000, // 1 minute
  });
}

/**
 * Resolve a sync conflict by pushing DB state to Auth0 (super_admin only)
 */
export function useResolveAuth0Conflict() {
  const api = useApi();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (userId: string) =>
      api.post<{ message: string }>(`/api/admin/auth0-sync/resolve/${userId}`, {}),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: userKeys.syncAudit() });
      queryClient.invalidateQueries({ queryKey: userKeys.syncStatus() });
    },
  });
}

/**
 * Invalidate the middleware user cache (super_admin only)
 */
export function useInvalidateAuth0Cache() {
  const api = useApi();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: () =>
      api.post<{ message: string; sizeBefore: number; sizeAfter: number }>(
        '/api/admin/auth0-sync/invalidate-cache',
        {}
      ),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: userKeys.syncStatus() });
    },
  });
}
